import { EditOutlined } from '@ant-design/icons';
import { App, Avatar, Button, Divider, Input, Popover, Space, Spin, Tag } from 'antd';
import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import UserAvatar from 'components/user-avatar';
import { assignSeat, listTickets } from '../api';
import { AdminSeat, AdminTable, GalaTicketRow, TICKET_STATUS_META, errorText } from '../contract';
import { seatLabel } from '../lib/seat-text';
import styles from '../styles.module.scss';
import { seatPersonName } from './seat-map';

/** Ghế đang mở hộp chọn + khung của nó (toạ độ trong khung sơ đồ) để neo Popover. */
export interface SeatPickTarget {
  seat: AdminSeat;
  table: AdminTable;
  box: { left: number; top: number; width: number; height: number };
}

interface Props {
  eventId: number;
  target: SeatPickTarget | null;
  /** Chữ điền sẵn vào ô tìm (vé vừa bấm trong danh sách "chưa có ghế"). */
  initialQuery?: string;
  /** `refocus` = trả tiêu điểm về ghế (đóng bằng Esc hoặc sau khi xếp), không phải bấm ra ngoài. */
  onClose: (refocus?: boolean) => void;
  /** Sau mỗi thay đổi: nạp lại sơ đồ. */
  onChanged: () => void | Promise<void>;
  /** Bấm "Chuyển ghế": vào chế độ chọn ghế đích. */
  onStartMove: (seat: AdminSeat, table: AdminTable) => void;
}

const SEARCH_DEBOUNCE_MS = 250;

/** Tên của vé để hiện trong danh sách chọn. */
const ticketName = (t: GalaTicketRow) => t.displayName || t.user?.nickName || `Vé #${t.id}`;

/** Ghế trống: tìm vé chưa có ghế (hoặc ghi tên tay) để xếp vào. */
const EmptySeatBody = ({
  eventId,
  seat,
  table,
  initialQuery,
  onDone,
}: {
  eventId: number;
  seat: AdminSeat;
  table: AdminTable;
  initialQuery?: string;
  onDone: () => void | Promise<void>;
}) => {
  const { message } = App.useApp();
  const [q, setQ] = useState(initialQuery ?? '');
  const [rows, setRows] = useState<GalaTicketRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [guest, setGuest] = useState('');
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    const mine = ++seq.current;
    if (!term) {
      setRows([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await listTickets(eventId, { q: term, seated: 0, page: 1, limit: 20 });
        if (mine === seq.current) setRows(res.data.filter((t) => t.status !== 'REVOKED'));
      } catch (e) {
        if (mine === seq.current) {
          setRows([]);
          message.error(errorText(e, 'Không tìm được vé.'));
        }
      } finally {
        if (mine === seq.current) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q, eventId, message]);

  const place = async (label: string, input: { ticketId: number } | { guestName: string }) => {
    setBusy(true);
    try {
      await assignSeat(eventId, seat.id, input);
      message.success(`Đã xếp ${label} vào ${seatLabel(table.name, seat.number)}.`);
      await onDone();
    } catch (e) {
      message.error(errorText(e, 'Không xếp được ghế.'));
    } finally {
      setBusy(false);
    }
  };

  const saveGuest = () => {
    const name = guest.trim();
    if (name) place(name, { guestName: name });
  };

  return (
    <>
      <Input.Search
        allowClear
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Tìm tên, @username hoặc UID"
        aria-label="Tìm vé chưa có ghế"
      />
      <div className={styles.seatPickList} aria-live="polite">
        {searching ? (
          <div className={styles.seatPickEmpty}>
            <Spin size="small" />
          </div>
        ) : !q.trim() ? (
          <div className={styles.seatPickEmpty}>Gõ để tìm vé chưa có ghế.</div>
        ) : rows.length === 0 ? (
          <div className={styles.seatPickEmpty}>Không có vé chưa xếp nào khớp.</div>
        ) : (
          rows.map((t) => (
            <button
              key={t.id}
              type="button"
              className={styles.seatPickRow}
              disabled={busy}
              onClick={() => place(ticketName(t), { ticketId: t.id })}
            >
              <UserAvatar src={t.user?.avatar} size={32} />
              <span className={styles.seatPickText}>
                <strong>{ticketName(t)}</strong>
                <span className={styles.subtle}>
                  {t.user ? `@${t.user.username}` : 'chưa gắn'}
                  {t.tiktokUid ? ` · UID ${t.tiktokUid}` : ''}
                </span>
              </span>
              {t.guestGroup && <Tag>{t.guestGroup}</Tag>}
            </button>
          ))
        )}
      </div>
      <Divider plain className={styles.seatPickDivider}>
        Ghi tên tay
      </Divider>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          value={guest}
          maxLength={120}
          onChange={(e) => setGuest(e.target.value)}
          onPressEnter={saveGuest}
          placeholder="Tên khách không có vé"
          aria-label="Tên ghi tay"
        />
        <Button type="primary" loading={busy} disabled={!guest.trim()} onClick={saveGuest}>
          Xếp
        </Button>
      </Space.Compact>
    </>
  );
};

/** Ghế có người: thẻ người ngồi + Bỏ ghế / Chuyển ghế. */
const TakenSeatBody = ({
  eventId,
  seat,
  table,
  onDone,
  onStartMove,
}: {
  eventId: number;
  seat: AdminSeat;
  table: AdminTable;
  onDone: () => void | Promise<void>;
  onStartMove: (seat: AdminSeat, table: AdminTable) => void;
}) => {
  const { message } = App.useApp();
  const [busy, setBusy] = useState(false);
  const t = seat.ticket;
  const name = seatPersonName(seat) ?? '';
  const status = t ? TICKET_STATUS_META[t.status] : null;

  const unseat = async () => {
    setBusy(true);
    try {
      await assignSeat(eventId, seat.id, {});
      message.success(`Đã bỏ ghế của ${name}.`);
      await onDone();
    } catch (e) {
      message.error(errorText(e, 'Không bỏ được ghế.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={styles.seatPickCard}>
        {seat.ticketId !== null ? (
          <UserAvatar src={t?.avatar} size={40} />
        ) : (
          <Avatar size={40} icon={<EditOutlined />} style={{ background: '#B7A4E8' }} />
        )}
        <span className={styles.seatPickText}>
          <strong>{name}</strong>
          <span className={styles.subtle}>
            {seat.ticketId === null
              ? 'Tên ghi tay, không có vé'
              : [
                  t?.username ? `@${t.username}` : 'chưa gắn',
                  t?.tiktokUid ? `UID ${t.tiktokUid}` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
          </span>
          <span>
            {status ? (
              <Tag color={status.color}>{status.text}</Tag>
            ) : seat.ticketId === null ? (
              <Tag color="purple">Tên tay</Tag>
            ) : null}
            {t?.guestGroup && <Tag>{t.guestGroup}</Tag>}
          </span>
        </span>
      </div>
      <Space className={styles.seatPickActions}>
        <Button danger loading={busy} onClick={unseat}>
          Bỏ ghế
        </Button>
        <Button disabled={busy} onClick={() => onStartMove(seat, table)}>
          Chuyển ghế
        </Button>
      </Space>
    </>
  );
};

/**
 * Khung nội dung hộp chọn: mở ra thì đưa tiêu điểm vào ô / nút đầu tiên (autoFocus không ăn
 * khi Popover còn đang hiện dần), để dùng bàn phím liền mạch từ ghế.
 */
const PickerFrame = ({ children }: { children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      ref.current?.querySelector<HTMLElement>('input, button:not([disabled])')?.focus();
    }, 80);
    return () => clearTimeout(timer);
  }, []);
  return (
    <div ref={ref} className={styles.seatPicker}>
      {children}
    </div>
  );
};

/**
 * Popover chọn người neo vào ghế vừa bấm (một khối vô hình đặt đúng chỗ ghế trên sơ đồ).
 * Esc hoặc bấm ra ngoài để đóng.
 */
const SeatPicker = ({ eventId, target, initialQuery, onClose, onChanged, onStartMove }: Props) => {
  const done = async () => {
    onClose(true);
    await onChanged();
  };

  // Esc đóng hộp chọn dù tiêu điểm đang ở ghế hay trong hộp.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const open = !!target;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const anchorStyle: CSSProperties = target
    ? { ...target.box, position: 'absolute' }
    : { display: 'none' };

  return (
    <Popover
      open={!!target}
      trigger="click"
      placement="right"
      destroyOnHidden
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={target ? seatLabel(target.table.name, target.seat.number) : null}
      content={
        target && (
          <PickerFrame key={target.seat.id}>
            {seatPersonName(target.seat) === null ? (
              <EmptySeatBody
                eventId={eventId}
                seat={target.seat}
                table={target.table}
                initialQuery={initialQuery}
                onDone={done}
              />
            ) : (
              <TakenSeatBody
                eventId={eventId}
                seat={target.seat}
                table={target.table}
                onDone={done}
                onStartMove={onStartMove}
              />
            )}
          </PickerFrame>
        )
      }
    >
      <div className={styles.seatAnchor} style={anchorStyle} aria-hidden />
    </Popover>
  );
};

export default SeatPicker;
